// src/components/vehicles/VehicleRepairHistory.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { api, formatDate, formatCurrency } from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";
import "../../css/Fleet.css";

const VehicleRepairHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "Administrator";
  const [vehicle, setVehicle] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, rRes] = await Promise.all([
          api.get(`/vehicles/${id}`),
          api.get(`/vehicles/${id}/repairs`),
        ]);
        setVehicle(vRes.data);
        setRepairs(Array.isArray(rRes.data) ? rRes.data : []);
      } catch (err) {
        console.error(err);
        toast.current?.show({ severity: "error", summary: "Błąd", detail: "Nie udało się pobrać danych" });
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const totalCost = repairs.reduce((sum, r) => sum + (r.cost || 0), 0);

  return (
    <div className="fleet-page fleet-animate-in">
      <Toast ref={toast} />

      <div className="fleet-page-header">
        <div>
          <Button icon="pi pi-arrow-left" className="p-button-text" style={{ marginRight: "0.5rem" }} onClick={() => navigate("/naprawy")} tooltip="Wróć do listy napraw" />
          <h1 style={{ display: "inline" }}>
            <span className="fleet-header-icon">🔧</span>
            Historia napraw
            {vehicle && <> — {vehicle.brand} {vehicle.model} ({vehicle.registration_number})</>}
          </h1>
          {vehicle && (
            <p className="fleet-page-subtitle">
              {vehicle.vehicleTypeName && <>{vehicle.vehicleTypeName} · </>}
              {vehicle.companyName && <>{vehicle.companyName} · </>}
              {vehicle.driver_operator && <>Kierowca: {vehicle.driver_operator}</>}
            </p>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="fleet-stats-grid" style={{ marginBottom: "1.25rem" }}>
        <div className="fleet-stat-card fleet-stat-card--primary">
          <div className="fleet-stat-icon fleet-stat-icon--primary"><i className="pi pi-wrench" /></div>
          <div className="fleet-stat-value fleet-stat-value--primary">{repairs.length}</div>
          <div className="fleet-stat-label">Łącznie napraw</div>
        </div>
        {isAdmin && <div className="fleet-stat-card fleet-stat-card--danger">
          <div className="fleet-stat-icon fleet-stat-icon--danger"><i className="pi pi-wallet" /></div>
          <div className="fleet-stat-value fleet-stat-value--danger" style={{ fontSize: "1.2rem" }}>{formatCurrency(totalCost)}</div>
          <div className="fleet-stat-label">Łączny koszt</div>
        </div>}
      </div>

      {/* Table */}
      <div className="fleet-card">
        <div className="fleet-card-body--flush fleet-table">
          <DataTable
            value={repairs} loading={loading}
            emptyMessage="Brak napraw dla tego pojazdu"
            responsiveLayout="stack" breakpoint="768px"
            paginator rows={20}
            sortMode="single" sortField="repair_date" sortOrder={-1}
          >
            <Column field="categoryName" header="Kategoria" body={(row) => (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
                {row.categoryName === 'Przegląd' && <i className="pi pi-sync" style={{ color: "var(--fleet-success)", fontSize: "0.85rem" }} />}
                {row.categoryName || '—'}
              </span>
            )} />
            <Column header="Data" body={(row) => formatDate(row.repair_date)} sortable sortField="repair_date" />
            <Column header="Koszt" body={(row) => (
              <span style={{ fontWeight: 600 }}>{formatCurrency(row.cost)}</span>
            )} />
            <Column field="description" header="Opis" style={{ maxWidth: "400px" }} />
            <Column header="Przebieg" body={(row) => row.mileage ? `${row.mileage} km` : row.motor_hours ? `${row.motor_hours} MTG` : "—"} />
            <Column field="createdByName" header="Dodał" />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default VehicleRepairHistory;
